import React from "react";
import { motion } from "framer-motion";
import SEO, { buildLocalBusinessJsonLd } from "@/components/SEO";
import { PageHeader, Section } from "@/components/Primitives";
import { BUSINESS } from "@/data/config";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

const TEAM_IMAGE =
  "https://images.pexels.com/photos/34670926/pexels-photo-34670926.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";

export default function About() {
  return (
    <>
      <SEO
        title="About Spokane Gutter Co. | Family Owned Gutter Crew"
        description={`${BUSINESS.name} is a family owned gutter and roofing crew based in Spokane, WA. Learn how we work and who is on the ladder.`}
        path="/about"
        jsonLd={buildLocalBusinessJsonLd()}
      />
      <PageHeader
        overline="About the shop"
        title="A small crew doing careful work on Spokane roof lines."
        lede={`${BUSINESS.name} started in ${BUSINESS.founded} out of a single truck and a used seamless machine. We still keep the crew small on purpose. That way the person who quotes your job is the person on the ladder the day it goes in.`}
        image={TEAM_IMAGE}
        alt="Portrait of a construction worker in a safety vest"
      />

      <Section testid="about-values">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            {
              t: "Roots in the Valley",
              b: "The shop is off Sprague. The truck sits in a lot in Millwood. Every crew member lives inside the service area they work in.",
            },
            {
              t: "Licensed and insured",
              b: "Washington State contractor license. Full liability and workers comp. Certificates emailed with every quote if you need them.",
            },
            {
              t: "Reviews we can back up",
              b: "Real customers. Real names. No filtered lists. Read the ones we did not love and how we fixed them.",
            },
          ].map((v, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="border-t border-white/10 pt-8"
            >
              <span className="font-display text-4xl text-copper-500 block mb-4">0{i + 1}</span>
              <h3 className="font-display text-2xl md:text-3xl mb-3 tracking-tightest">{v.t}</h3>
              <p className="text-bone-300 leading-relaxed">{v.b}</p>
            </motion.div>
          ))}
        </div>
      </Section>

      <Section testid="about-cta" className="bg-ink-900 border-y border-white/10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          <div className="md:col-span-8">
            <p className="overline mb-4">Ready to get started</p>
            <h2 className="font-display text-4xl md:text-6xl leading-[0.95] tracking-tightest">
              Book a walk around. We show up with a ladder and a notepad.
            </h2>
          </div>
          <div className="md:col-span-4 flex md:justify-end">
            <Link
              to="/contact"
              data-testid="about-cta-quote"
              className="group inline-flex items-center gap-3 bg-copper-500 hover:bg-copper-600 text-ink-950 hover:text-white font-heading font-semibold text-base px-7 py-4 rounded-sm"
            >
              Request a Free Quote
              <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform" />
            </Link>
          </div>
        </div>
      </Section>
    </>
  );
}
